import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, first, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { PurposeEnum } from 'src/app/models/shared-models';
import { BaseWizardComponent } from '../base-wizard.component';
import { ICategoryPurposeDataModel, CategoryPurposeDataModel, INotificationAreaLimitModel, NotificationAreaLimitModel, IPreviousNotificationModel, LotPlanModel, ILotPlanModel, PurposeModel } from '../models/veg-wizard-models';
import { CategoryService } from '../services/category-service';
import { Logger } from '../services/logger-service';
import { NotificationAreaLimitService } from '../services/notification-area-limit-service';
import { UtilService } from '../services/util.service';

import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-select-category',
  templateUrl: './select-category.component.html',
  styleUrls: ['./select-category.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
})

export class SelectCategoryComponent extends BaseWizardComponent implements OnInit {
  form: FormGroup;

  get formInputNotificationAreaValidControl() { return this.form.controls['formInputNotificationAreaValid']; }
  get formInputFodderValidControl() { return this.form.controls['formInputFodderValid']; }
  get formInputThinningValidControl() { return this.form.controls['formInputThinningValid']; }

  categoriesRefData: ICategoryPurposeDataModel[] = [];

  purposeCode = '';
  purposeIsThinning = false;

  // keeps track of the selected category purpose ids, updated as the user selects them.
  selectedcategoryPurposeIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  showPreviousNotifications = false;
  isProposedCategorySelected = false;
  shouldShowCoordinatedProjectsMessage = false;
  shouldShowIsNotificationAreaWarningMessage = false
  shouldShowIsNotificationAreaInfoMessage = false;
  selectedCategoryPurposesAreInAllowedLGAList = false
  shouldLoadNotificationAreaLimit = false;
  prevNotificationsAreSetToShow = false;
  previousNotifications: IPreviousNotificationModel[] = [];
  lotOnPlan: ILotPlanModel = new LotPlanModel();
  formControlCategoryNames: string[] = [];

  private notificationClearingAreaLimit: INotificationAreaLimitModel | undefined = undefined;

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private categoryService: CategoryService,
    private notificationAreaLimitService: NotificationAreaLimitService,
    private utilService: UtilService,
    private logger: Logger,
    router: Router) {
    super();

    this.form = new FormGroup({});
  }

  ngOnInit(): void {
    const purposeWrapper = this.vwds.getPurpose();
    this.lotOnPlan = this.vwds.getLotPlanModel() || new LotPlanModel();

    if (!this.lotOnPlan || !purposeWrapper) {
      throw new Error('Lot Plan and purpose is required to select categories');
    }

    this.purposeCode = purposeWrapper?.purpose?.purposeCode ?? '';
    this.purposeIsThinning = this.purposeCode === PurposeEnum.thinning;
    this.shouldLoadNotificationAreaLimit = this.categoryService.shouldLoadNotificationAreaLimit(purposeWrapper?.purpose);

    this.form = this.formBuilder.group({

      // input form controls to capture form related validations / errors
      formInputNotificationAreaValid: null,
      formInputFodderValid: null,
      formInputThinningValid: null
    });

    this.setCategoriesRefData$()
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.initForm();
      });
  }

  private initForm() {
    this.form.setValidators([this.atLeastOneCategoryPurposeSelectedValidator()]);
    const categoryPurposesFromSession: ICategoryPurposeDataModel[] = this.vwds.getCategories();

    this.formControlCategoryNames = [];
    for (const category of this.categoriesRefData) {

      let defaultCheckBoxValue = false;
      if (!!categoryPurposesFromSession && categoryPurposesFromSession.length > 0) {
        // find the categoryPurpose record in the session, assign true boolean value for checkbox
        if (categoryPurposesFromSession.some(x => x.categoryPurposeId === category.categoryPurposeId)) {
          defaultCheckBoxValue = true;
        }
      }

      const catControl = new FormControl(defaultCheckBoxValue);
      const formControlName = `categoryPurposeId_${category.categoryPurposeId}`;
      this.form.addControl(formControlName, catControl);
      this.formControlCategoryNames.push(formControlName); // keep track of the formControl names in local array
    }

    if (this.form?.value) {
      // on first load, check if the form has been populated from previous session and trigger selectedcategoryPurposeIds$
      const selectedPurposeCategories = this.getSelectedCategoryPurposeIds();
      this.selectedcategoryPurposeIds$.next(selectedPurposeCategories);
    }

    this.performValidations(); // initial check & validations

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const selectedPurposeCategories = this.getSelectedCategoryPurposeIds();
        this.selectedcategoryPurposeIds$.next(selectedPurposeCategories);
      });

    // recalculate the behaviour of the forms based on the selected Category items
    this.selectedcategoryPurposeIds$
      .pipe(untilDestroyed(this),
        tap((selectedPurposeIds) => {
          this.isProposedCategorySelected = selectedPurposeIds.some(id => this.getCategoryPurpose(id).isProposed);
        }),
        switchMap(selectedCategoryPurposeIds => {
          if (this.selectedcategoryPurposeIds$.getValue().length > 0 && this.shouldLoadNotificationAreaLimit) {
            return this.getNotificationAreaLimit$(selectedCategoryPurposeIds);
          }
          return of(undefined);
        }),
        switchMap((notificationAreaLimit: INotificationAreaLimitModel | undefined) => {

          this.notificationClearingAreaLimit = notificationAreaLimit;
          this.checkNotificationAreaWarningMessage();
          this.checkNotificationAreaInfoMessage();

          if (this.prevNotificationsAreSetToShow) {
            return this.getPreviousNotification$(this.getSelectedCategories(), this.lotOnPlan, this.purposeCode);
          }
          return of([] as IPreviousNotificationModel[]);
        }),
        tap((previousNotifications: IPreviousNotificationModel[]) => {
          this.previousNotifications = previousNotifications;
          this.showPreviousNotifications = this.prevNotificationsAreSetToShow && this.previousNotifications?.length > 0;
          this.shouldShowCoordinatedProjectsMessage = this.showCoordinatedProjectsMessage();
          this.selectedCategoryPurposesAreInAllowedLGAList = this.allSelectedLotPlanPurposeCategoriesIsWithinCorrectLocalGovernmentAreas();
        }))
      .subscribe(() => {
        this.performValidations();
      });
  }

  getNotificationClearingAreaLimitMessage(): string {
    const selectedCategoriesModel = this.getSelectedCategories();
    return this.categoryService.getNotificationAreaLimitMessage(this.notificationClearingAreaLimit,
      selectedCategoriesModel, this.vwds.getPurpose()?.purpose ?? new PurposeModel());
  }

  performValidations() {

    // set specific areas depending on the form input valid controls
    this.formInputNotificationAreaValidControl.setErrors(null);
    this.formInputFodderValidControl.setErrors(null);
    this.formInputThinningValidControl.setErrors(null);

    if (this.shouldShowIsNotificationAreaWarningMessage) this.formInputNotificationAreaValidControl.setErrors({ 'Required': true });
    if (!this.selectedCategoryPurposesAreInAllowedLGAList) this.formInputFodderValidControl.setErrors({ 'Required': true });
    if (this.purposeIsThinning) this.formInputThinningValidControl.setErrors({ 'Required': true });
  }

  private getPreviousNotification$(
    categoryPurposes: ICategoryPurposeDataModel[],
    lotOnPlan: ILotPlanModel,
    purposeCode: string): Observable<IPreviousNotificationModel[]> {

    if (!lotOnPlan) { this.logger.error('Lot On Plan data is empty/undefined!', {}); return of([]); }
    if (!purposeCode) { this.logger.error('PurposeCode is empty/undefined!', {}); return of([]); }
    if (!categoryPurposes) { this.logger.error('Purpose Categories data is empty/undefined!', {}); return of([]); }

    const categoryCodes = categoryPurposes.map(x => x.categoryCode);

    return this.categoryService.getPreviousNotifications$(lotOnPlan.lot, lotOnPlan.plan,
      purposeCode, categoryCodes, true)
      .pipe(first(), untilDestroyed(this),
        map((result) => {
          return result?.notifications ?? [];
        }));
  }

  private getNotificationAreaLimit$(categoryPurposeIds: number[]): Observable<INotificationAreaLimitModel | undefined> {
    if (categoryPurposeIds.length == 0) return of(undefined);

    return this.notificationAreaLimitService.getNotificationAreaLimit$(categoryPurposeIds, null);
  }

  private getSelectedCategoryPurposeIds() {
    const formValue = this.form.getRawValue();
    const selectedPurposeCategories: number[] = [];

    for (const prop of Object.keys(formValue)) {
      if (!prop.startsWith('categoryPurposeId_')) { continue; }
      if (!formValue[prop]) { continue; }

      const categoryPurposeId = +prop.split('_')[1]; //ensure positive number
      selectedPurposeCategories.push(categoryPurposeId);
    }

    return selectedPurposeCategories;
  }

  private checkNotificationAreaInfoMessage() {
    const notificationAreaMessage = this.getNotificationClearingAreaLimitMessage();

    this.shouldShowIsNotificationAreaInfoMessage = this.hasNotReachedClearingAreaLimitCheck(this.notificationClearingAreaLimit)
      && this.notificationClearingAreaLimit !== null
      && !!notificationAreaMessage && notificationAreaMessage != ''
      && this.selectedcategoryPurposeIds$.getValue().length > 0;
  }

  private checkNotificationAreaWarningMessage() {
    const notificationAreaMessage = this.getNotificationClearingAreaLimitMessage();

    this.shouldShowIsNotificationAreaWarningMessage = !this.hasNotReachedClearingAreaLimitCheck(this.notificationClearingAreaLimit)
      && !!notificationAreaMessage && notificationAreaMessage != ''
      && this.selectedcategoryPurposeIds$.getValue().length > 0;
  }

  private hasNotReachedClearingAreaLimitCheck(notificationClearingAreaLimit: NotificationAreaLimitModel | undefined): boolean {
    if (!notificationClearingAreaLimit) return false;

    return notificationClearingAreaLimit?.remainingAreaLimitInHectares > 0;
  }

  private showCoordinatedProjectsMessage() {
    return this.purposeCode === PurposeEnum.coordinatedProjects
      && this.previousNotifications.length > 0;
  }

  private setCategoriesRefData$(): Observable<ICategoryPurposeDataModel[]> {
    return this.categoryService.getCategoriesForPurpose$(this.purposeCode)
      .pipe(
        tap((result: ICategoryPurposeDataModel[]) => {
          if (!result) {
            this.logger.warning('There are no valid Categories... please check', {});
          }
          else {
            this.categoriesRefData = result;
            this.prevNotificationsAreSetToShow = this.categoriesRefData?.some(x => x.showsPreviousNotifications);
          }
        })
      )
  }

  private getCategoryPurpose(id: number): ICategoryPurposeDataModel {
    return this.categoriesRefData?.find(i => i.categoryPurposeId === id) ?? new CategoryPurposeDataModel();
  }

  private getSelectedCategories(): ICategoryPurposeDataModel[] {
    return this.selectedcategoryPurposeIds$.getValue()
      .map((x: number) => this.getCategoryPurpose(x)) ?? new CategoryPurposeDataModel;
  }

  private allSelectedLotPlanPurposeCategoriesIsWithinCorrectLocalGovernmentAreas(): boolean {
    const selectedCategoryPurposes = this.getSelectedCategories();

    if (selectedCategoryPurposes.length == 0) { return true; } //return true because user has not selected anything!

    if (selectedCategoryPurposes.some(x => !x.withinCorrectLocalGovernmentAreas)) {
      this.logger.warning('Cannot continue forward due to LotPlan CategoryPurpose is not in the allowed LGA list', {});
      return false;
    }

    return true;
  }

  /**
   * Custom validator - At least one category must be selected
   */
  atLeastOneCategoryPurposeSelectedValidator(): ValidatorFn {
    return () => {
      const selectedCategories = this.getSelectedCategoryPurposeIds();
      return selectedCategories.length ? null : { categorySelectionRequiredValidationError: true };
    };
  }

  override next(): void {
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
    if (!this.form.valid) {
      this.utilService.getFormValidationErrors(this.form);
      console.log('form is not valid! Please check errors');
      return;
    }

    this.vwds
      .setCategories$(this.getSelectedCategories())
      .pipe(
        first(),
        untilDestroyed(this),
        mergeMap(() => this.vwds.setSelfAuditRequired$())
      )
      .subscribe(() => {
        super.next();
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }

}
